import type { MetaDescriptor, MetaFunction } from "@remix-run/node";

/**
 * Helper to merge parent meta tags with route-specific meta tags.
 * This ensures viewport and other important meta tags are inherited from root.
 *
 * AIDEV-NOTE: Remix doesn't automatically merge meta tags - routes override parent meta
 *
 * @param metaFn - Function that returns route-specific meta tags
 * @returns MetaFunction that merges parent and route meta tags
 */
export const mergeMeta =
    (
        metaFn: (args: Parameters<MetaFunction>[0]) => MetaDescriptor[],
    ): MetaFunction =>
    (args) => {
        const routeMeta = metaFn(args);
        const parentMeta = args.matches
            .flatMap((match) => match.meta ?? [])
            .filter((meta) => {
                // Filter out meta tags that the route is explicitly overriding
                if ("title" in meta) {
                    return !routeMeta.some((m) => "title" in m);
                }
                if ("name" in meta && meta.name) {
                    return !routeMeta.some(
                        (m) => "name" in m && m.name === meta.name,
                    );
                }
                if ("property" in meta && meta.property) {
                    return !routeMeta.some(
                        (m) => "property" in m && m.property === meta.property,
                    );
                }
                return true;
            });

        // Route meta takes precedence over parent meta
        return [...parentMeta, ...routeMeta];
    };

/**
 * Creates a MetaFunction that only sets the title while preserving all parent meta tags
 */
export const titleMeta = (title: string): MetaFunction =>
    mergeMeta(() => [{ title }]);

/**
 * Creates a MetaFunction that sets title and description while preserving other parent meta tags
 */
export const pageMeta = (title: string, description: string): MetaFunction =>
    mergeMeta(() => [{ title }, { name: "description", content: description }]);
